import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { autosize, PRIORITY_META } from "./playbookUtils";
import { PriorityMenu } from "./PriorityMenu";

export const ActionRow = ({
  action,
  isOff,
  gripRef,
  onTextChange,
  onPriorityChange,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onGripKeyDown,
  isDragSource,
  isDragOver,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);

  // Autosize on mount and whenever the value changes (N2) — the effect with
  // no explicit text dependency also covers "selection switch" since the
  // parent remounts/rerenders this row when a different signal is selected.
  useEffect(() => {
    autosize(textareaRef.current);
  });

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleDocMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, [menuOpen]);

  const meta = PRIORITY_META[action.priority] || PRIORITY_META.medium;

  return (
    <div
      data-testid={`action-row-${action._key}`}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      className={`flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white py-2.5 pl-[11px] pr-3 ${
        isDragOver
          ? "outline outline-dashed outline-2 -outline-offset-1 outline-blue-300"
          : ""
      } ${isDragSource ? "opacity-45" : "opacity-100"}`}
      style={{ borderLeft: `3px solid ${isOff ? "#D1D5DB" : meta.color}` }}
    >
      <button
        type="button"
        data-testid={`grip-${action._key}`}
        ref={gripRef}
        draggable="true"
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onKeyDown={onGripKeyDown}
        aria-label={t("pages.settings.signalsPlaybooks.editor.reorderAria")}
        title={t("pages.settings.signalsPlaybooks.editor.reorderTitle")}
        className="mt-0.5 flex h-[26px] w-[22px] flex-none cursor-grab items-center justify-center rounded-md border-none bg-transparent text-gray-300 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        ⋮⋮
      </button>

      <div ref={wrapperRef} className="relative mt-px flex-none">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          <span className={`h-2 w-2 flex-none rounded-full ${meta.dotClass}`} />
          <span>
            {t(`pages.settings.signalsPlaybooks.priority.${action.priority}`)}
          </span>
          <span className="text-[9px] text-gray-400">▾</span>
        </button>
        {menuOpen && (
          <PriorityMenu
            current={action.priority}
            onChoose={onPriorityChange}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </div>

      <textarea
        ref={textareaRef}
        data-testid={`action-text-${action._key}`}
        aria-label={t("pages.settings.signalsPlaybooks.editor.actionTextLabel")}
        value={action.text}
        placeholder={t(
          "pages.settings.signalsPlaybooks.editor.actionPlaceholder",
        )}
        rows={1}
        onChange={(e) => {
          autosize(e.target);
          onTextChange(e.target.value);
        }}
        className={`min-w-0 flex-1 resize-none overflow-hidden border-none bg-transparent p-0.5 text-[13.5px] leading-5 outline-none focus:ring-0 ${
          isOff ? "text-gray-400" : "text-gray-900"
        }`}
      />

      <button
        type="button"
        onClick={onRemove}
        aria-label={t("pages.settings.signalsPlaybooks.editor.removeAction")}
        title={t("pages.settings.signalsPlaybooks.editor.removeAction")}
        className="mt-0.5 flex h-[26px] w-[26px] flex-none items-center justify-center rounded-md border-none bg-transparent text-gray-300 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        ×
      </button>
    </div>
  );
};
